// S9 Hiding rules > "Found on your pages" (module-11 4a, CT-028, REQ-10.9), the parts with no React: the list's query
// (GET /api/apps/:appId/masking-finds, keyed by `q` and `page`, so each search and page is its own entry), the paging
// line, the line under an allowed find, and the toast after a switch or rename. PATCH .../masking-finds/:findId
// answers the find, not the AppView, so a write invalidates every page of the list (maskingFindsKey) instead of
// replacing the app's cache.
import {
  MASKING_FINDS_PAGE_SIZE,
  type MaskingFindList,
  type MaskingFindView,
  type UpdateMaskingFindInput,
} from '@feedback/shared/contracts/apps';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api, seg } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/format';

/** The search asks the server this long after the last keystroke. */
export const FIND_SEARCH_DEBOUNCE_MS = 300;

/** Every search and page of an app's finds: what a switch or rename invalidates. */
export const maskingFindsKey = (appId: string) => ['app', appId, 'masking-finds'] as const;

export const findPath = (appId: string, findId?: string) =>
  `/api/apps/${seg(appId)}/masking-finds${findId ? `/${seg(findId)}` : ''}`;

export const maskingFindsQuery = (appId: string, q: string, page: number) =>
  queryOptions({
    queryKey: [...maskingFindsKey(appId), { q, page }],
    queryFn: () =>
      api.get<MaskingFindList>(findPath(appId), { q: q || undefined, page, pageSize: MASKING_FINDS_PAGE_SIZE }),
    // The rows stay while the next page or search loads, so the list doesn't jump.
    placeholderData: keepPreviousData,
  });

/** "Showing 26–50 of 60". */
export function pageRangeText(page: number, pageSize: number, total: number): string {
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  return `Showing ${formatNumber(first)}–${formatNumber(last)} of ${formatNumber(total)}`;
}

/** Under an allowed find: "Allowed by Alex Chen, 16 Oct 2026". */
export function allowedByText(find: Pick<MaskingFindView, 'stateChangedBy' | 'stateChangedAt'>): string {
  const by = find.stateChangedBy ? ` by ${find.stateChangedBy.displayName}` : '';
  const when = find.stateChangedAt ? `, ${formatDate(find.stateChangedAt)}` : '';
  return `Allowed${by}${when}`;
}

/** The toast after a write: the switch, a rename, or "Use the original". */
export function findSavedText(body: UpdateMaskingFindInput): string {
  if (body.state === 'hidden') {
    return 'Hidden in screenshots';
  }
  if (body.state === 'allowed') {
    return 'Allowed in screenshots';
  }
  return body.description === null ? 'Original description restored' : 'Description saved';
}
